import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Calendar, AlertCircle, Circle } from 'lucide-react';
import { cn } from '../lib/utils';
import { ICON_MAP, BADGE_COLORS } from '../components/visualizations/visualizationConstants';
import { BulletShapePublic } from '../components/visualizations/BulletShape';

const API = process.env.REACT_APP_BACKEND_URL;

// Determine if a hex color is dark (for contrast decisions)
const isColorDark = (hex) => {
  if (!hex || hex.length < 7) return false;
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 < 128;
};

// Calculate spine left for public view children container
// Container has pl-6 (24px). Spine must bisect child icon centers.
const getPublicChildSpineLeft = (parentLevel, displayStyle) => {
  const childLevel = Math.min((parentLevel || 1) + 1, 5);
  if (displayStyle === 'bullets') {
    const center = { 2: 7, 3: 6, 4: 5, 5: 5 }[childLevel] || 7;
    return 24 + center - 1; // 24 = pl-6
  }
  // Public icon centers: lv2=20px(w-10), lv3=16px(w-8), lv4=14px(w-7), lv5=12px(w-6)
  const center = { 2: 20, 3: 16, 4: 14, 5: 12 }[childLevel] || 20;
  return 24 + center - 1; // 24 = pl-6
};

// Timeline Item Component for Public View — respects ALL branding properties
const PublicTimelineItem = ({
  item,
  depth = 0,
  primaryColor,
  secondaryColor,
  textColor,
  dateColor,
  displayStyle,
  lineConfig,
}) => {
  const [expanded, setExpanded] = useState(true);

  const IconComponent = ICON_MAP[item.icon] || Circle;
  const hasChildren = item.children && item.children.length > 0;
  const badgeColorClass = item.badge?.color ? BADGE_COLORS[item.badge.color] : BADGE_COLORS.blue;
  const isBullets = displayStyle === 'bullets';

  const iconSize = { 1: 'w-12 h-12', 2: 'w-10 h-10', 3: 'w-8 h-8', 4: 'w-7 h-7', 5: 'w-6 h-6' }[item.level] || 'w-10 h-10';

  const titleSize = { 1: 'text-xl font-bold', 2: 'text-lg font-semibold', 3: 'text-base font-medium', 4: 'text-sm font-medium', 5: 'text-sm' }[item.level] || 'text-base';

  return (
    <div className="relative">
      <div className="relative flex items-start gap-5 py-4">
        {/* Timeline dot — icons or bullets */}
        <div className="flex flex-col items-center relative z-10">
          {isBullets ? (
            <BulletShapePublic
              bulletStyle={item.bullet_style || 'dot'}
              color={primaryColor}
              level={item.level}
            />
          ) : (
            <div
              className={cn("rounded-full flex items-center justify-center shadow-md flex-shrink-0", iconSize)}
              style={{ backgroundColor: primaryColor, color: 'white' }}
            >
              <IconComponent className={cn(
                item.level === 1 ? 'w-6 h-6' :
                item.level === 2 ? 'w-5 h-5' :
                'w-4 h-4'
              )} />
            </div>
          )}

          {/* Connector to children */}
          {hasChildren && expanded && (() => {
            const childLc = (lineConfig || []).find(c => c.level === (depth + 2));
            if (!childLc?.enabled) return null;
            return (
              <div
                className="flex-1 mt-2"
                style={{
                  width: childLc.style === 'dashed' ? 0 : 2,
                  backgroundColor: childLc.style === 'dashed' ? 'transparent' : secondaryColor,
                  borderLeft: childLc.style === 'dashed' ? `2px dashed ${secondaryColor}` : 'none',
                }}
              />
            );
          })()}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 pt-1">
          {item.date_display && (
            <p className="text-sm mb-1" style={{ color: dateColor || '#6b7280' }}>
              {item.date_display}
            </p>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            <h4 className={titleSize} style={{ color: textColor || '#111827' }}>
              {item.title}
            </h4>
            {item.badge && (
              <span className={cn("text-xs px-2.5 py-1 rounded-full font-medium", badgeColorClass)}>
                {item.badge.text}
              </span>
            )}
          </div>

          {item.description && (
            <div
              className={cn("mt-2 leading-relaxed prose prose-sm max-w-none prose-p:my-0.5 prose-ul:my-0.5 prose-ol:my-0.5 prose-li:my-0", item.level <= 2 ? "[&>*]:text-base" : "[&>*]:text-sm")}
              style={textColor ? { color: textColor, opacity: 0.8 } : { color: '#4b5563' }}
              dangerouslySetInnerHTML={{ __html: item.description }}
            />
          )}
        </div>
      </div>

      {/* Children */}
      {hasChildren && expanded && (
        <div className="ml-6 pl-6 relative">
          {/* Spine line — per-level config */}
          {(() => {
            const childLc = (lineConfig || []).find(c => c.level === (depth + 2));
            if (!childLc?.enabled) return null;
            return (
              <div 
                className="absolute top-0 bottom-0 pointer-events-none"
                style={{
                  left: getPublicChildSpineLeft(item.level, displayStyle),
                  borderLeft: `2px ${childLc.style} ${secondaryColor}`,
                }}
              />
            );
          })()}
          {item.children.map((child, index) => (
            <PublicTimelineItem
              key={child.item_id || index}
              item={child}
              depth={depth + 1}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              textColor={textColor}
              dateColor={dateColor}
              displayStyle={displayStyle}
              lineConfig={lineConfig}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const VisualizationPublicView = () => {
  const { shareToken } = useParams();
  const [visualization, setVisualization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVisualization = async () => {
      try {
        const response = await fetch(`${API}/api/visualizations/public/${shareToken}`);

        if (response.ok) {
          const data = await response.json();
          setVisualization(data);
        } else {
          const err = await response.json();
          setError(err.detail || 'Visualization not found');
        }
      } catch (err) {
        console.error('Error fetching visualization:', err);
        setError('Failed to load visualization');
      } finally {
        setLoading(false);
      }
    };

    fetchVisualization();
  }, [shareToken]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-500">Loading visualization...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Visualization Not Available
          </h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!visualization) return null;

  // Extract all branding properties
  const { branding = {}, timeline_items = [], name, published_at } = visualization;
  const primaryColor = branding.primary_color || '#8b5cf6';
  const secondaryColor = branding.secondary_color || '#c4b5fd';
  const textColor = branding.text_color || null;
  const dateColor = branding.date_color || null;
  const backgroundColor = branding.background_color || null;
  const displayStyle = branding.display_style || 'icons';
  const lineConfig = branding.line_config || [
    { level: 1, enabled: true, style: 'dashed' },
    { level: 2, enabled: true, style: 'dashed' },
    { level: 3, enabled: true, style: 'dashed' },
  ];
  const logoPosition = branding.logo_position || 'left';
  const logoUrl = branding.logo_url || null;

  // Page background: prefer explicit background_color, fallback to background_style
  const bgStyle = branding.background_style || 'light';
  const pageBg = backgroundColor || (bgStyle === 'dark' ? '#111827' : '#f9fafb');
  const isDark = backgroundColor ? isColorDark(backgroundColor) : bgStyle === 'dark';

  // Derived contrast colors for header/footer chrome
  const chromeTextColor = textColor || (isDark ? '#f9fafb' : '#111827');
  const chromeMutedColor = isDark ? '#9ca3af' : '#6b7280';
  const chromeBorderColor = isDark ? '#374151' : '#e5e7eb';
  const chromeHeaderBg = isDark ? '#1f2937' : '#ffffff';

  return (
    <div className="min-h-screen" style={{ backgroundColor: pageBg }}>
      {/* Header */}
      <header className="border-b py-6" style={{ backgroundColor: chromeHeaderBg, borderColor: chromeBorderColor }}>
        <div className="max-w-4xl mx-auto px-6">
          {logoUrl && logoPosition === 'left' ? (
            /* Left: Logo on left, title next to it */
            <div className="flex items-center gap-[50px]">
              <img src={logoUrl} alt="Logo" className="h-[80px] w-auto object-contain shrink-0" data-testid="public-viz-logo" />
              <div>
                <h1 className="text-2xl font-bold" style={{ color: chromeTextColor }} data-testid="public-viz-title">
                  {name}
                </h1>
                {published_at && (
                  <p className="text-sm mt-1" style={{ color: chromeMutedColor }}>
                    Last updated: {new Date(published_at).toLocaleDateString('en-US', {
                      month: 'long', day: 'numeric', year: 'numeric'
                    })}
                  </p>
                )}
              </div>
            </div>
          ) : logoUrl && logoPosition === 'right' ? (
            /* Right: Title on left, logo on right */
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold" style={{ color: chromeTextColor }} data-testid="public-viz-title">
                  {name}
                </h1>
                {published_at && (
                  <p className="text-sm mt-1" style={{ color: chromeMutedColor }}>
                    Last updated: {new Date(published_at).toLocaleDateString('en-US', {
                      month: 'long', day: 'numeric', year: 'numeric'
                    })}
                  </p>
                )}
              </div>
              <img src={logoUrl} alt="Logo" className="h-[80px] w-auto object-contain shrink-0" data-testid="public-viz-logo" />
            </div>
          ) : logoUrl && logoPosition === 'center' ? (
            /* Center: Logo centered above, title on left */
            <div>
              <div className="flex justify-center mb-4">
                <img src={logoUrl} alt="Logo" className="h-[80px] w-auto object-contain" data-testid="public-viz-logo" />
              </div>
              <div>
                <h1 className="text-2xl font-bold" style={{ color: chromeTextColor }} data-testid="public-viz-title">
                  {name}
                </h1>
                {published_at && (
                  <p className="text-sm mt-1" style={{ color: chromeMutedColor }}>
                    Last updated: {new Date(published_at).toLocaleDateString('en-US', {
                      month: 'long', day: 'numeric', year: 'numeric'
                    })}
                  </p>
                )}
              </div>
            </div>
          ) : (
            /* No logo — just title */
            <div>
              <h1 className="text-2xl font-bold" style={{ color: chromeTextColor }} data-testid="public-viz-title">
                {name}
              </h1>
              {published_at && (
                <p className="text-sm mt-1" style={{ color: chromeMutedColor }}>
                  Last updated: {new Date(published_at).toLocaleDateString('en-US', {
                    month: 'long', day: 'numeric', year: 'numeric'
                  })}
                </p>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Timeline Content */}
      <main className="max-w-4xl mx-auto px-6 py-10">
        {timeline_items.length === 0 ? (
          <div
            className="text-center py-16 rounded-lg border-2 border-dashed"
            style={{ borderColor: chromeBorderColor }}
          >
            <Calendar className="w-12 h-12 mx-auto mb-4" style={{ color: chromeMutedColor }} />
            <p className="text-lg" style={{ color: chromeMutedColor }}>
              No timeline items yet
            </p>
          </div>
        ) : (
          <div className="relative space-y-2" data-testid="public-viz-timeline">
            {/* Timeline spine — connects root-level items */}
            {(() => { const lc1 = lineConfig.find(c => c.level === 1); return lc1?.enabled && timeline_items.length > 1 ? (
              <div 
                className="absolute top-0 bottom-0 pointer-events-none"
                style={{
                  left: displayStyle === 'bullets' ? 7 : 23,
                  borderLeft: `2px ${lc1.style} ${secondaryColor}`,
                }}
              />
            ) : null; })()}
            {timeline_items.map((item, index) => (
              <PublicTimelineItem
                key={item.item_id || index}
                item={item}
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                textColor={textColor}
                dateColor={dateColor}
                displayStyle={displayStyle}
                lineConfig={lineConfig}
              />
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t py-6" style={{ backgroundColor: chromeHeaderBg, borderColor: chromeBorderColor }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-sm" style={{ color: chromeMutedColor }}>
            Powered by <span className="font-medium" style={{ color: primaryColor }}>StackRanked</span>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default VisualizationPublicView;
